import { GENERAL_TEXT } from '@/texts/catalog/general';

export const normalizeStatusLabel = (
  value,
  {
    activeLabel = GENERAL_TEXT.status.active,
    inactiveLabel = GENERAL_TEXT.status.inactive,
    defaultLabel = GENERAL_TEXT.status.default,
  } = {}
) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['activo', 'active', '1', 'true'].includes(normalized)) {
      return activeLabel;
    }

    if (['inactivo', 'inactive', '0', 'false'].includes(normalized)) {
      return inactiveLabel;
    }
  }

  if (typeof value === 'number') {
    return value === 1 ? activeLabel : inactiveLabel;
  }

  if (typeof value === 'boolean') {
    return value ? activeLabel : inactiveLabel;
  }

  return defaultLabel;
};

export const normalizeYesNoLabel = (
  value,
  {
    yesLabel = GENERAL_TEXT.yesNo.yes,
    noLabel = GENERAL_TEXT.yesNo.no,
    emptyLabel = GENERAL_TEXT.yesNo.empty,
  } = {}
) => {
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (['si', 'sí', 'yes', 'true', '1'].includes(normalized)) {
      return yesLabel;
    }

    if (['no', 'false', '0'].includes(normalized)) {
      return noLabel;
    }
  }

  if (typeof value === 'number') {
    return value === 1 ? yesLabel : noLabel;
  }

  if (typeof value === 'boolean') {
    return value ? yesLabel : noLabel;
  }

  return emptyLabel;
};

export const toBooleanFlag = (value) => {
  if (typeof value === 'boolean') {
    return value;
  }

  if (typeof value === 'number') {
    return value === 1;
  }

  const normalized = String(value || '').trim().toLowerCase();
  return ['1', 'true', 'si', 'sí', 'yes'].includes(normalized);
};

const isPlainObject = (value) => value !== null && typeof value === 'object' && !Array.isArray(value);

const parseJsonObject = (value) => {
  if (typeof value !== 'string') {
    return null;
  }

  try {
    const parsed = JSON.parse(value);
    return isPlainObject(parsed) ? parsed : null;
  } catch (error) {
    return null;
  }
};

const extractCandidatePayload = (response) => {
  if (!response) {
    return {};
  }

  if (typeof response === 'string') {
    return parseJsonObject(response) || {};
  }

  if (!isPlainObject(response)) {
    return {};
  }

  if (isPlainObject(response.response)) {
    return extractCandidatePayload(response.response);
  }

  if (isPlainObject(response.data)) {
    return response.data;
  }

  const parsedData = parseJsonObject(response.data);
  if (parsedData) {
    return parsedData;
  }

  return response;
};

const hasEnvelopeDataField = (payload) => isPlainObject(payload) && Object.prototype.hasOwnProperty.call(payload, 'data');

const isDataObjectPayload = (payload) => {
  return hasEnvelopeDataField(payload) && isPlainObject(payload.data);
};

export const extractPayload = (response) => {
  const payload = resolveApiPayload(response);

  // Keep list envelopes intact when data is an array so callers can access
  // sibling fields like pagination/type/message.
  if (hasEnvelopeDataField(payload) && Array.isArray(payload.data)) {
    return payload;
  }

  // Unwrap object payloads for detail/forms where data contains the entity.
  if (isDataObjectPayload(payload)) {
    return payload.data;
  }

  return isPlainObject(payload) ? payload : {};
};

export const extractCollection = (response) => {
  const payload = extractPayload(response);
  const root = isDataObjectPayload(payload)
    ? payload.data
    : payload;

  if (Array.isArray(root?.data)) {
    return root.data;
  }

  if (Array.isArray(root)) {
    return root;
  }

  return [];
};

export const resolveApiPayload = (response) => {
  return extractCandidatePayload(response);
};

export const extractApiErrorMessage = (response, fallbackMessage = '') => {
  const payload = resolveApiPayload(response);
  const type = String(payload?.type || '').trim().toLowerCase();
  const message = String(payload?.message || '').trim();
  const nestedPayload = payload?.data && typeof payload.data === 'object'
    ? payload.data
    : null;
  const nestedType = String(nestedPayload?.type || '').trim().toLowerCase();
  const nestedMessage = String(nestedPayload?.message || '').trim();

  if (type === 'error') {
    return message || fallbackMessage;
  }

  if (nestedType === 'error') {
    return nestedMessage || message || fallbackMessage;
  }

  const errors = payload?.errors ?? nestedPayload?.errors;
  if (Array.isArray(errors) && errors.length > 0) {
    const firstMessage = String(errors[0] || '').trim();
    if (firstMessage !== '') {
      return firstMessage;
    }
  }

  if (errors && typeof errors === 'object') {
    const firstKey = Object.keys(errors)[0] || '';
    const firstValue = firstKey ? errors[firstKey] : null;
    if (Array.isArray(firstValue) && firstValue.length > 0) {
      const firstMessage = String(firstValue[0] || '').trim();
      if (firstMessage !== '') {
        return firstMessage;
      }
    }

    const flatMessage = String(firstValue || '').trim();
    if (flatMessage !== '') {
      return flatMessage;
    }
  }

  return '';
};

export const extractApiSuccessMessage = (response, fallbackMessage = '') => {
  const payload = resolveApiPayload(response);
  const resolveMessage = (value) => {
    if (typeof value === 'string') {
      return value.trim();
    }

    if (!value || typeof value !== 'object') {
      return '';
    }

    const directCandidates = [
      value.message,
      value.msg,
      value.text,
      value.title,
      value.success,
      value.detail,
    ];

    for (const candidate of directCandidates) {
      if (typeof candidate === 'string' && candidate.trim() !== '') {
        return candidate.trim();
      }
    }

    const firstStringEntry = Object.values(value).find(
      (candidate) => typeof candidate === 'string' && candidate.trim() !== ''
    );

    return typeof firstStringEntry === 'string' ? firstStringEntry.trim() : '';
  };

  const rootData = payload?.data;
  const firstDataRow = Array.isArray(rootData)
    ? rootData.find((item) => isPlainObject(item)) || null
    : null;

  const messageCandidates = [
    payload?.message,
    payload?.msg,
    payload?.text,
    payload?.title,
    payload?.success,
    payload?.detail,
    isPlainObject(rootData) ? rootData.message : null,
    isPlainObject(rootData) ? rootData.msg : null,
    isPlainObject(rootData) ? rootData.text : null,
    isPlainObject(rootData) ? rootData.title : null,
    isPlainObject(rootData) ? rootData.success : null,
    isPlainObject(rootData) ? rootData.detail : null,
    firstDataRow?.message,
    firstDataRow?.msg,
    firstDataRow?.text,
    firstDataRow?.title,
    firstDataRow?.success,
    firstDataRow?.detail,
  ];

  const message = messageCandidates
    .map((candidate) => resolveMessage(candidate))
    .find((candidate) => candidate !== '') || '';

  return message || fallbackMessage;
};